import { useQuery } from "@tanstack/react-query";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

/**
 * 파견·병동이동 등 기간 배정 한 건.
 * 발행 스냅샷 `nurses[].inbound[]` / `roster.nurses[].inbound[]` API: `target_group_id` · `target_group_name`
 */
export type InboundItem = {
  startDate?: string;
  endDate?: string | null;
  start_date?: string;
  end_date?: string | null;
  reason: string;
  target_group_id?: string;
  target_group_name?: string;
  /** 레거시·내부 더미 등 */
  targetGroupId?: string;
  targetGroupName?: string;
};

export type InboundInfo = {
  inbound_list: InboundItem[];
};

export type RosterScheduleCell =
  | string
  | null
  | {
      code: string | null;
      reason: string;
    };

// 스케줄 간호사 데이터 타입
export type NurseSchedule = {
  count?: { [key: string]: number | null } | null;
  counts?: Record<string, number>;
  experience: number | null;
  id: string;
  name: string;
  schedule: RosterScheduleCell[];
  schedule_ids: Array<number | null>;
  is_inbound?: boolean;
  /** 구형 `{ inbound_list }` · 신형 배열 `inbound: [...]` */
  inbound?: InboundInfo | InboundItem[];
  /** 기간 배정(페이로드) — 항목 형태는 `inbound_list` 와 동일. 셀 기간 바는 `inbound`만 사용 */
  assignments?: InboundItem[];
};

// 스케줄 데이터 타입
export type RosterSchedule = {
  days_in_month: number;
  memo: null | string;
  month: number;
  nurses: NurseSchedule[];
  schedule_id: string;
  shift_colors: { [key: string]: string };
  violations: unknown[];
  year: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function resignationEntries(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (entry) => isRecord(entry) && String(entry.reason ?? "") === "퇴사"
  );
}

export default function useRosterSchedule({
  scheduleId,
  groupId,
  enabled,
}: {
  scheduleId: string | null | undefined;
  groupId?: string | null;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: QUERY_KEYS.roster.schedule(
      scheduleId as string,
      groupId as string
    ),
    queryFn: async () => {
      if (!scheduleId) return null;
      const data = await httpClient.get<RosterSchedule>(
        `/roster/schedule/${scheduleId}`,
        {
          params: {
            group_id: groupId,
          },
        }
      );

      if (import.meta.env.DEV && data) {
        const rowSchedules = data.nurses.map((nurse) => ({
          nurseId: nurse.id,
          name: nurse.name,
          schedule: nurse.schedule,
          scheduleIds: nurse.schedule_ids,
          assignments: nurse.assignments ?? [],
          inbound: nurse.inbound ?? [],
        }));
        const rowAssignments = data.nurses
          .map((nurse) => ({
            nurseId: nurse.id,
            name: nurse.name,
            assignments: resignationEntries(nurse.assignments),
            inbound: resignationEntries(nurse.inbound),
          }))
          .filter(
            (row) => row.assignments.length > 0 || row.inbound.length > 0
          );

        console.info("[resignation-debug] GET /roster/schedule/:scheduleId", {
          scheduleId,
          groupId,
          year: data.year,
          month: data.month,
          rowSchedules,
          rowAssignments,
        });
      }

      return data;
    },
    enabled: enabled !== false && !!scheduleId,
    staleTime: 1000 * 60 * 5, // 5분간 캐시
  });
}
